<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, shallowRef, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import Dropbox from '@uppy/dropbox';
import StreamService from '@/services/StreamService';
import UserService from '@/services/UserService';
import utils from '@/utils';
import { loadMediaInfoLib } from '@/Utils/mediaInfoLoader';
import { APP_EVENTS, emitAppEvent, onAppEvent } from '@/Utils/appEventBus';
import { verboseUpload } from '@/Utils/verboseUploadLog';
import {
  UPPY_ALLOWED_VIDEO_FILE_TYPES,
  VIDEO_UPLOAD_ACCEPT,
  MP4_ONLY_ERROR_MESSAGE,
  isAllowedVideoFile,
} from '@/Utils/videoUploadFormats';
import { useNotification } from '@kyvg/vue3-notification';
import IconBase from '@/components/icon/IconBase.vue';
import IconCross from '@/components/icon/IconCross.vue';
import IconUpload from '@/components/icon/IconUpload.vue';
import IconPlus from '@/components/icon/IconPlus.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import IconCheckmarkStroke from '@/components/icon/IconCheckmarkStroke.vue';
import IconFolderOutline from '@/components/icon/IconFolderOutline.vue';
import IconAlertTriangle from '@/components/icon/IconAlertTriangle.vue';
import FormButton from '@/components/Atoms/FormButton.vue';

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';

// A single, app-wide Uppy uploader that stays alive across navigation so uploads
// run in the background. Two states: an expanded dashboard panel and a minimized
// progress chip (bottom-right). Any upload surface opens it via the
// OPEN_VOD_UPLOADER app event with a { stream } payload.
// Optional `replace: { videoId, oldFileSize, fileName, precedence, currFileId }`
// switches the widget into VOD replace mode (max 1 file).

const store = useStore();
const router = useRouter();
const { notify } = useNotification();
const uppy = shallowRef(null);
const dashboardEl = ref(null);
const fileInput = ref(null);
const expanded = ref(false);
const currentStream = shallowRef(null);
const replaceContext = shallowRef(null);
// Non-blocking quality warning for pre-recorded (scheduled) uploads only —
// same gate as legacy `shouldCheckMediaQuality` (ScheduledStreamManageDashboard).
// Flags files that aren't CBR or have no audio track.
const qualityWarnings = ref([]); // [{ id, vbr, noAudio }]
const hasQualityWarning = computed(() => qualityWarnings.value.length > 0);
const clearQualityWarnings = () => { qualityWarnings.value = []; };
// Shown in the empty drop-zone area when a staged file exceeds the bitrate limit.
const bitrateWarning = ref(null); // { fileName, kbps, message }
const clearBitrateWarning = () => { bitrateWarning.value = null };
// Only mention the issues that actually apply to the staged files.
const qualityIssue = computed(() => {
  const vbr = qualityWarnings.value.some((w) => w.vbr);
  const noAudio = qualityWarnings.value.some((w) => w.noAudio);
  if (vbr && noAudio) return 'should be constant-bitrate (CBR) and include an audio track';
  if (vbr) return 'should be constant-bitrate (CBR)';
  return 'should include an audio track';
});

const pickFiles = () => { if (fileInput.value) fileInput.value.click(); };
const onFilesPicked = (e) => {
  const files = Array.from((e.target && e.target.files) || []);
  // `accept` on the input is only a hint (bypassable), so re-check every file
  // and reject non-MP4 with a clear message before it reaches the queue.
  const allowed = files.filter((f) => isAllowedVideoFile(f));
  if (allowed.length !== files.length) {
    notify({ group: 'error', text: MP4_ONLY_ERROR_MESSAGE });
  }
  allowed.forEach((f) => {
    try {
      uppy.value.addFile({ name: f.name, type: f.type, data: f, source: 'Local' });
    } catch (err) { /* duplicate/restriction — Uppy surfaces it */ }
  });
  if (e.target) e.target.value = '';
};

// Platform max for uploaded video bitrate (kbps). Enforced client-side via
// MediaInfo before any bytes are sent to storage.
const MAX_VIDEO_BITRATE_KBPS = 22000;
const MULTIPART_THRESHOLD = 25 * 1024 * 1024;

/** Resolve video bitrate in kbps from MediaInfo fields (values are in bps). */
const resolveVideoBitrateKbps = (info, fileSize = 0) => {
  let bps = info?.videoBitrate || 0;
  if (!bps && info?.overallBitrate) {
    bps = Math.max(0, info.overallBitrate - (info.audioBitrate || 0));
  }
  // Last resort: average from file size + duration when container tags omit BitRate.
  if (!bps && info?.duration > 0 && fileSize > 0) {
    bps = Math.round((fileSize * 8) / info.duration);
  }
  return Math.round(bps / 1000);
};

const bitrateLimitError = (kbps) =>
  `Video bitrate (${kbps.toLocaleString()} kbps) exceeds the maximum supported limit of ${MAX_VIDEO_BITRATE_KBPS.toLocaleString()} kbps.`;

const assertBitrateWithinLimit = (info, fileSize = 0) => {
  const kbps = resolveVideoBitrateKbps(info, fileSize);
  if (kbps > MAX_VIDEO_BITRATE_KBPS) {
    throw new Error(bitrateLimitError(kbps));
  }
  return kbps;
};
// Fallback S3 part size used only when the backend spec omits `chunkSize`.
// Restored to 50 MB (the legacy default the VOD backend presigns against) so
// uploads over the Fastly edge use fewer, larger parts instead of ~6× more 8 MB
// parts — the smaller size added per-part overhead and, combined with the
// per-part timeout below, kept tripping Uppy's rate limiter (see `signPart`).
// Used only when the backend spec omits `chunkSize`; when present, the backend's
// `spec.chunkSize` wins on both the Uppy path (via getChunkSizeOption +
// chunkSizeByData) and the raw-fetch replace path (`uploadMultipartPresigned`).
const FALLBACK_CHUNK_SIZE = 50 * 1024 * 1024;
// Per-part upload timeout used ONLY by the raw-fetch (replace) and direct axios
// paths — NOT the Uppy AwsS3 path (see `signPart` for why). A part that makes no
// progress fails (and is retried) instead of hanging forever — the failure mode
// of the old raw-fetch/axios paths on long-haul links. Derived from the part
// size with a slow-link floor (~20 KB/s) and a 120 s minimum so genuinely
// slow-but-working parts still finish.
const MIN_UPLOAD_BPS = 20 * 1024;
const partTimeoutMs = (bytes) => Math.max(120000, Math.ceil((bytes || FALLBACK_CHUNK_SIZE) / MIN_UPLOAD_BPS) * 1000);
// Per-file working data captured at add-time so background uploads stay bound to
// the folder they were started from: { stream, subfolder, mediaInfo, precedence, spec }
const metaByFile = new Map();

// Uppy's AwsS3 multipart minimum part size (mirrors MultipartUploader #minPartSize).
const MIN_PART_SIZE = 5 * 1024 * 1024;
// Maps a file's data blob -> the backend's chunk size, so getChunkSize can honor
// `spec.chunkSize`. Needed because Uppy calls getChunkSize with the file *data*
// blob (`new MultipartUploader(file.data, …)`), NOT the Uppy file, so a
// `metaByFile.get(file.id)` lookup there always missed and every upload used the
// 50 MB fallback — ignoring the size the backend presigned against (e.g. 25 MB).
const chunkSizeByData = new WeakMap();
// The chunk size the Uppy AwsS3 path uses for a file. Resolves the backend's
// `spec.chunkSize` via the data blob (what Uppy passes), falling back to the
// Uppy file id and finally FALLBACK_CHUNK_SIZE. The verbose log derives its part
// count from this SAME function so what it reports matches what Uppy really does.
const getChunkSizeOption = (fileOrData) => {
  if (fileOrData) {
    const byData = chunkSizeByData.get(fileOrData);
    if (byData) return byData;
    const meta = metaByFile.get(fileOrData.id);
    if (meta && meta.spec && meta.spec.chunkSize) return meta.spec.chunkSize;
  }
  return FALLBACK_CHUNK_SIZE;
};
// Number of parts Uppy actually creates for a local multipart upload — mirrors
// MultipartUploader.#initChunks (arraySize floor + trailing partial, 10k cap).
const clientPartCount = (fileSize, chunkSize) => {
  if (!fileSize || fileSize <= MIN_PART_SIZE) return 1;
  let cs = Math.max(chunkSize || FALLBACK_CHUNK_SIZE, MIN_PART_SIZE);
  if (Math.floor(fileSize / cs) > 10000) cs = fileSize / 10000;
  return Math.max(1, Math.ceil(fileSize / cs));
};

// Local VOD replace bypasses Uppy AwsS3 — replace API returns direct /
// multipart-presigned / AWS multipart (not always Fastly). Track in-flight
// custom replaces so Uppy's `complete` handler doesn't clear context early.
let localReplaceInFlight = 0;

const ensureAwsSdk = () => new Promise((resolve, reject) => {
  if (window.AWS) { resolve(window.AWS); return; }
  const existing = document.getElementById('aws-sdk-js');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.AWS));
    existing.addEventListener('error', reject);
    return;
  }
  const script = document.createElement('script');
  script.id = 'aws-sdk-js';
  script.src = 'https://sdk.amazonaws.com/js/aws-sdk-2.663.0.min.js';
  script.onload = () => resolve(window.AWS);
  script.onerror = reject;
  document.head.appendChild(script);
});

// Sub-folder the user is looking at when the file is added (visual grouping —
// the file itself still uploads into the folder stream). Only applies when the
// open route is the same folder the upload targets.
const activeSubfolderFor = (stream) => {
  const route = router.currentRoute.value;
  if (!stream || stream._playoutDir || stream.type !== 'vod') return null;
  if (route?.params?.streamId !== stream._id) return null;
  return route?.query?.subfolder || null;
};

// Ids saved in this session that the playlist endpoint may not list yet.
// A single refresh right after upload often comes back without the new file.
const pendingUploadedIds = new Set();
let uploadReconcileTimers = [];

const playlistHasFile = (list, fileId) =>
  (Array.isArray(list) ? list : []).some((f) => f.id === fileId || f._id === fileId);

const rememberUploadedFile = (stream, file, uploadResult, subfolder, snapshot = {}) => {
  const id = uploadResult?.fileId || uploadResult?.id;
  if (!id || !stream || stream._playoutDir) return;
  pendingUploadedIds.add(id);
  const viewing = router.currentRoute.value.params?.streamId;
  if (viewing === stream._id) {
    store.commit('Streams/ADD_VOD_FILE', {
      ...(uploadResult && typeof uploadResult === 'object' ? uploadResult : {}),
      id,
      fileName: uploadResult?.fileName || snapshot.fileName || file?.data?.name || file?.name || 'video.mp4',
      bytes: uploadResult?.bytes || snapshot.fileSize || file?.data?.size || file?.size || 0,
      creationTime: uploadResult?.creationTime || new Date().toISOString(),
      mediaInfo: uploadResult?.mediaInfo || snapshot.mediaInfo || null,
      enabled: uploadResult?.enabled !== false,
      subfolder: subfolder || uploadResult?.subfolder || null,
    });
  }
};

const persistUploadedFileInFolder = async (stream, fileId, snapshot = {}, subfolder = null) => {
  if (!stream?._id || !fileId || stream._playoutDir) return;

  let listed = [];
  try {
    listed = await store.dispatch('Streams/getFilesInVODFolder', {
      streamId: stream._id,
      ensureIds: [fileId],
    });
  } catch (e) { listed = []; }

  if (!playlistHasFile(listed, fileId)) {
    try {
      await StreamService.saveStreamPlaylistVideo(stream._id, {
        id: fileId,
        fileName: snapshot.fileName || 'video.mp4',
        bytes: snapshot.fileSize || 0,
        mediaInfo: snapshot.mediaInfo,
        subfolder: subfolder || null,
      });
      listed = await store.dispatch('Streams/getFilesInVODFolder', {
        streamId: stream._id,
        ensureIds: [fileId],
      });
    } catch (e) {
      console.error('add uploaded video to folder playlist failed: ', e);
    }
  }
  if (playlistHasFile(listed, fileId)) pendingUploadedIds.delete(fileId);

  const author = store.state.User?.activeTeam
    ? store.state.User?.teamMemberUser?.email
    : store.state.User?.email;
  if (author && playlistHasFile(listed, fileId)) {
    try {
      await StreamService.updateStreamPlaylistVideoMetaData(stream._id, fileId, { author });
    } catch (e) { /* author is best-effort */ }
  }
};

const reconcileUploadedFiles = async (streamId) => {
  const id = streamId || router.currentRoute.value.params?.streamId;
  if (!id) return;
  const ensureIds = [...pendingUploadedIds];
  try {
    const files = await store.dispatch('Streams/getFilesInVODFolder', ensureIds.length ? { streamId: id, ensureIds } : { streamId: id });
    const list = Array.isArray(files) ? files : [];
    ensureIds.forEach((fileId) => {
      if (list.some((f) => f.id === fileId || f._id === fileId)) pendingUploadedIds.delete(fileId);
    });
  } catch (e) { /* keep the optimistic row until a later refresh */ }
};

const scheduleUploadReconcile = (streamId) => {
  uploadReconcileTimers.forEach((timer) => clearTimeout(timer));
  const run = () => { reconcileUploadedFiles(streamId); };
  // The playlist record lags the save. Retry until the new file is listed.
  uploadReconcileTimers = [1500, 4000].map((delay) => setTimeout(run, delay));
};

const stats = reactive({
  total: 0, completed: 0, failed: 0, pending: 0, uploading: false, progress: 0,
  totalBytes: 0, uploadedBytes: 0, speed: 0, eta: 0,
});

// Rolling sample for upload speed / ETA
let lastSampleTime = 0;
let lastSampleBytes = 0;
const resetSpeedSample = () => { lastSampleTime = 0; lastSampleBytes = 0; stats.speed = 0; stats.eta = 0; };

const fmtBytes = (n) => {
  if (!n || n <= 0) return '0 MB';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1000)));
  return `${(n / Math.pow(1000, i)).toFixed(i >= 2 ? 1 : 0)} ${u[i]}`;
};

// Uppy Dashboard labels sizes with binary (1024) units via prettier-bytes.
// Rewrite each item's status size with our decimal (1000) fmtBytes so the list
// matches the footer / OS file info.
const syncDashboardFileSizes = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const inst = uppy.value;
      if (!inst || !dashboardEl.value) return;
      inst.getFiles().forEach((file) => {
        const size = file.size || (file.data && file.data.size) || 0;
        if (!size) return;
        const item = document.getElementById(`uppy_${file.id}`);
        const sizeEl = item && item.querySelector('.uppy-Dashboard-Item-statusSize');
        if (sizeEl) sizeEl.textContent = fmtBytes(size);
      });
    });
  });
};

const fmtEta = (s) => {
  s = Math.max(0, Math.round(s));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const visible = computed(() => expanded.value || stats.total > 0);
const showChip = computed(() => !expanded.value && stats.total > 0);
const allDone = computed(() => stats.total > 0 && stats.completed + stats.failed >= stats.total);
const folderName = computed(() => {
  const s = currentStream.value;
  if (!s) return '';
  // For playout, show "Playout name - Directory" for full context.
  if (s._playoutDir && s._playoutDirName) return `${s.name} - ${s._playoutDirName}`;
  return s.name || '';
});

const streamKeyOf = (s) => (s.type === 'scheduled' ? s._id : (s.key || s._id));

// Cloud import (Dropbox) is only offered for VOD folders (content library / video
// hosting) — not for pre-recorded/scheduled or other flows.
const isVodContext = () => !!(currentStream.value && currentStream.value.type === 'vod');
const syncCloudProvider = () => {
  if (!uppy.value) return;
  const existing = uppy.value.getPlugin('Dropbox');
  if (isVodContext()) {
    if (!existing) {
      uppy.value.use(Dropbox, {
        id: 'Dropbox',
        target: Dashboard,
        companionUrl: process.env.VUE_APP_COMPANION_API_BASE_URL,
        companionHeaders: { 'auth-token': UserService.getUserToken() },
      });
    }
  } else if (existing) {
    uppy.value.removePlugin(existing);
  }
};

const inspect = (fileData) =>
  loadMediaInfoLib().then(
    (lib) => new Promise((resolve) => utils.inspectMediaFile(lib, fileData, (info) => resolve(info)))
  );

// Inspect a staged local file: hard-block over-bitrate files immediately, and
// for pre-recorded streams only, warn (without blocking) when it isn't CBR or
// has no audio. Media info is cached so preProcess doesn't re-inspect.
const checkQuality = async (file) => {
  if (file.isRemote || !file.data) return;
  const stream = (metaByFile.get(file.id) || {}).stream || currentStream.value;
  try {
    const info = await inspect(file.data);
    const meta = metaByFile.get(file.id) || { stream };
    meta._info = info;
    metaByFile.set(file.id, meta);

    const kbps = resolveVideoBitrateKbps(info, file.data.size);
    if (kbps > MAX_VIDEO_BITRATE_KBPS) {
      const msg = bitrateLimitError(kbps);
      bitrateWarning.value = { fileName: file.name, kbps, message: msg };
      if (uppy.value?.getFile(file.id)) {
        uppy.value.setFileState(file.id, { error: msg });
        uppy.value.emit('upload-error', uppy.value.getFile(file.id), new Error(msg));
        uppy.value.removeFile(file.id);
      }
      return;
    }
    // A valid file clears a previous bitrate rejection notice.
    clearBitrateWarning();

    if (!stream || stream.type !== 'scheduled') return;
    const vbr = !!info.isVbr;
    const noAudio = !info.hasAudioTrack;
    if ((vbr || noAudio) && !qualityWarnings.value.some((w) => w.id === file.id)) {
      qualityWarnings.value = [...qualityWarnings.value, { id: file.id, vbr, noAudio }];
    }
  } catch (e) { /* inspection is best-effort until preProcess */ }
};

const recompute = () => {
  const inst = uppy.value;
  if (!inst) return;
  const files = inst.getFiles();
  stats.total = files.length;
  stats.completed = files.filter((f) => f.progress && f.progress.uploadComplete).length;
  stats.failed = files.filter((f) => f.error).length;
  stats.uploading = files.some((f) => f.progress && f.progress.uploadStarted && !f.progress.uploadComplete);
  // Files that haven't started yet (can be uploaded even while others run —
  // e.g. added for a different folder mid-upload).
  stats.pending = files.filter((f) => !f.error && !(f.progress && f.progress.uploadStarted) && !(f.progress && f.progress.uploadComplete)).length;
  stats.totalBytes = files.reduce((a, f) => a + ((f.progress && f.progress.bytesTotal) || f.size || (f.data && f.data.size) || 0), 0);
  stats.uploadedBytes = files.reduce((a, f) => a + ((f.progress && f.progress.bytesUploaded) || 0), 0);

  // Speed / ETA from a time-sampled byte delta (smoothed)
  if (stats.uploading) {
    const now = Date.now();
    if (!lastSampleTime) {
      lastSampleTime = now;
      lastSampleBytes = stats.uploadedBytes;
    } else if (now - lastSampleTime >= 500) {
      const dt = (now - lastSampleTime) / 1000;
      const inst2 = Math.max(0, (stats.uploadedBytes - lastSampleBytes) / dt);
      stats.speed = stats.speed ? stats.speed * 0.6 + inst2 * 0.4 : inst2;
      stats.eta = stats.speed > 0 ? (stats.totalBytes - stats.uploadedBytes) / stats.speed : 0;
      lastSampleTime = now;
      lastSampleBytes = stats.uploadedBytes;
    }
  }
  syncDashboardFileSizes();
};

// ── beforeunload guard while uploading ──────────────────────────────────────
const beforeUnload = (e) => {
  if (stats.uploading) {
    e.preventDefault();
    e.returnValue = '';
    return '';
  }
  return undefined;
};

const preProcess = async (fileIDs) => {
  for (const id of fileIDs) {
    const file = uppy.value.getFile(id);
    if (!file) continue;
    // Remote (cloud) files are handled by Companion — no local inspect/signing.
    if (file.isRemote) continue;
    const meta = metaByFile.get(id) || {};
    const stream = meta.stream || currentStream.value;
    uppy.value.emit('preprocess-progress', file, { mode: 'indeterminate', message: 'Analyzing video…' });
    try {
      // eslint-disable-next-line no-await-in-loop
      const info = meta._info || await inspect(file.data);
      if (!info?.codecs?.find((c) => c.codec === 'AVC')) {
        throw new Error('Only MP4 videos encoded with H.264 (AVC) are supported.');
      }
      assertBitrateWithinLimit(info, file.data.size);
      const durationSec = info.duration;
      const durationMins = Math.ceil((durationSec < 60 ? 60 : durationSec) / 60);
      const mediaInfo = { ...info, durationMins };
      const precedence = Date.now();
      const useMultipart = file.data.size > MULTIPART_THRESHOLD;
      const payload = {
        encodingPresets: [],
        metadata: {
          size: file.data.size,
          type: file.data.type,
          originalFilename: file.data.name,
          precedence,
        },
        mediaInfo,
        streamType: stream.type,
        multipartSupport: useMultipart,
      };
      let spec;
      // Local VOD replace is handled by runLocalReplaceUpload (onBeforeUpload).
      // eslint-disable-next-line no-await-in-loop
      if (stream._playoutDir) {
        spec = await StreamService.requestFastlyPlayoutUploadUrl(stream._id, stream._playoutDir, payload);
      } else {
        spec = await StreamService.requestFastlyVodMediaUploadUrl(streamKeyOf(stream), payload);
      }
      metaByFile.set(id, {
        ...meta, stream,
        mediaInfo,
        precedence,
        spec,
        fileName: file.name || file.data?.name || meta.fileName,
        fileSize: file.data?.size || file.size || meta.fileSize,
      });
      // Bind the backend's chunk size to the file data so Uppy's getChunkSize
      // (called with the data blob, not the file) honors spec.chunkSize instead
      // of always using the 50 MB fallback. Keeps client chunking aligned with
      // the presigned part plan.
      if (file.data) chunkSizeByData.set(file.data, spec.chunkSize || FALLBACK_CHUNK_SIZE);
      // Report the ACTUAL chunking the Uppy path will use (not the backend's
      // presigned plan), and flag when they differ so the log explains why the
      // number of chunks doesn't match the presigned part count.
      const isMp = spec.uploadType === 'multipart-presigned';
      const actualChunk = isMp ? Math.max(getChunkSizeOption(file.data), MIN_PART_SIZE) : undefined;
      verboseUpload.beginFile(id, {
        name: file.name || (file.data && file.data.name),
        size: file.data && file.data.size,
        uploadType: spec.uploadType,
        totalParts: isMp ? clientPartCount(file.data && file.data.size, actualChunk) : 1,
        chunkSize: actualChunk,
        stream: stream && stream.name,
        queueTotal: fileIDs.length,
        presigned: isMp ? { parts: spec.parts && spec.parts.length, chunkSize: spec.chunkSize } : undefined,
      });
    } catch (e) {
      verboseUpload.endFile(id, { ok: false, error: e });
      const msg = e?.message || 'Failed to prepare upload.';
      if (msg.includes('maximum supported limit')) {
        const kbps = resolveVideoBitrateKbps(meta._info || {}, file.data?.size || 0);
        bitrateWarning.value = { fileName: file.name, kbps, message: msg };
      } else {
        notify({ group: 'error', text: msg });
      }
      if (uppy.value.getFile(id)) {
        uppy.value.emit('upload-error', uppy.value.getFile(id), e);
        uppy.value.removeFile(id);
      }
    }
  }
  fileIDs.forEach((id) => {
    const f = uppy.value.getFile(id);
    if (f) uppy.value.emit('preprocess-complete', f);
  });
};

const finalizeReplace = async (stream, replace, newVideoId, fileMeta) => {
  await StreamService.onCompleteVodReplacement(
    replace.videoId,
    newVideoId,
    stream._id,
    stream.key,
    fileMeta,
  );
  await StreamService.clearVodMetadata(stream._id, replace.videoId, replace.currFileId);
  notify({ group: 'success', text: 'Video replaced successfully.' });
  // Full reload so the detail preview/player picks up the new media.
  window.removeEventListener('beforeunload', beforeUnload);
  window.location.reload();
};

const uploadMultipartPresigned = async (spec, fileData, onBytes, file) => {
  const { parts, chunkSize, multipartUploadId, uploadId } = spec;
  const stream = currentStream.value;
  const streamKey = streamKeyOf(stream);
  const concurrency = fileData.size > 1024 * 1024 * 1024 ? 2 : 4;
  let totalUploaded = 0;
  const completedParts = [];
  const partQueue = [...(parts || [])];
  const fileId = file && file.id;
  const totalParts = (parts || []).length;

  const uploadNextPart = async () => {
    while (partQueue.length > 0) {
      const part = partQueue.shift();
      const start = (part.partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, fileData.size);
      const blob = fileData.slice(start, end);
      let attempts = 0;
      let success = false;
      while (attempts < 3 && !success) {
        // Abort a stalled part instead of hanging forever (raw fetch has no
        // timeout); the existing retry/backoff below then re-attempts it.
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), partTimeoutMs(end - start));
        const ctx = verboseUpload.beginPart({ fileId, partNumber: part.partNumber, totalParts, bytes: end - start });
        let response = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          response = await fetch(part.url, {
            method: 'PUT',
            headers: { 'Content-Type': fileData.type },
            body: blob,
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`Part failed: ${response.status}`);
          const etag = response.headers.get('etag') || '';
          completedParts.push({ partNumber: part.partNumber, etag: etag.replace(/"/g, '') });
          verboseUpload.endPart(ctx, { status: response.status, etag: etag.replace(/"/g, '') });
          success = true;
        } catch (e) {
          verboseUpload.failPart(ctx, { error: e, status: response && response.status });
          attempts += 1;
          if (attempts >= 3) throw e;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, attempts * 2000));
        } finally {
          clearTimeout(timer);
        }
      }
      totalUploaded += (end - start);
      onBytes(Math.min((totalUploaded / fileData.size) * 100, 99.9));
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, Math.max(partQueue.length, 1)) }, () => uploadNextPart()),
  );
  completedParts.sort((a, b) => a.partNumber - b.partNumber);
  if (stream._playoutDir) {
    await StreamService.completeFastlyPlayoutMultipartUpload(
      stream._id, stream._playoutDir, uploadId, multipartUploadId, completedParts, stream.type,
    );
  } else {
    await StreamService.completeFastlyMultipartUpload(
      streamKey, uploadId, multipartUploadId, completedParts, stream.type,
    );
  }
  onBytes(100);
  return true;
};

const uploadMultipartAws = async (spec, fileData, onBytes) => {
  const AWS = await ensureAwsSdk();
  const creds = spec.uploadCredentials || {};
  AWS.config.update({
    region: creds.region,
    useAccelerateEndpoint: creds.useAccelerateEndpoint,
    // Bound each part request so a stalled connection fails and is retried by the
    // SDK (with exponential backoff) instead of hanging.
    maxRetries: 4,
    httpOptions: { timeout: partTimeoutMs(creds.chunkSize || (2 * 1000 * 1000)) },
  });
  AWS.config.credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: creds.poolId,
    LoginId: UserService.getUserEmail(),
  });
  const uploadBucket = new AWS.S3({ params: { Bucket: creds.bucketName } });
  return new Promise((resolve, reject) => {
    const handle = uploadBucket.upload({
      Key: creds.keyPrefix + spec.uploadId,
      ContentType: fileData.type,
      Body: fileData,
      queueSize: creds.concurrency || 2,
      partSize: creds.chunkSize || (2 * 1000 * 1000),
      UseAccelerateEndpoint: true,
    });
    handle.on('httpUploadProgress', (ev) => {
      if (ev.total) onBytes((ev.loaded * 100) / ev.total);
    });
    handle.send((err) => (err ? reject(err) : resolve(true)));
  });
};

const setLocalUploadProgress = (fileId, pct, fileSize) => {
  if (!uppy.value) return;
  // The file may no longer be in Uppy's state (e.g. cancelled/removed mid-flight).
  // setFileState throws for unknown ids, so skip silently — progress UI is best-effort.
  if (!uppy.value.getFile(fileId)) return;
  uppy.value.setFileState(fileId, {
    progress: {
      uploadStarted: Date.now(),
      uploadComplete: pct >= 100,
      percentage: pct,
      bytesUploaded: Math.round((pct / 100) * fileSize),
      bytesTotal: fileSize,
    },
  });
  recompute();
};

// Same three upload types as UploadFiles.replace — do not route through Uppy
// AwsS3 Fastly adapters or replace-complete points at a missing object.
const runLocalReplaceUpload = async (file) => {
  const stream = currentStream.value;
  const replace = replaceContext.value;
  if (!stream || !replace?.videoId || !file?.data) {
    throw new Error('Replace upload is missing stream or file data.');
  }
  localReplaceInFlight += 1;
  submitting.value = true;
  window.addEventListener('beforeunload', beforeUnload);
  try {
    setLocalUploadProgress(file.id, 0, file.data.size);
    const info = await inspect(file.data);
    if (!info?.codecs?.find((c) => c.codec === 'AVC')) {
      throw new Error('Only MP4 videos encoded with H.264 (AVC) are supported.');
    }
    assertBitrateWithinLimit(info, file.data.size);
    const durationMins = Math.ceil(((info.duration < 60 ? 60 : info.duration)) / 60);
    const mediaInfo = { ...info, durationMins };
    const payload = {
      encodingPresets: [],
      metadata: {
        size: file.data.size,
        type: file.data.type,
        originalFilename: file.data.name,
        precedence: replace.precedence ?? Date.now(),
      },
      mediaInfo,
      streamType: stream.type,
      multipartSupport: file.data.size > MULTIPART_THRESHOLD,
    };
    const spec = await StreamService.requestVodMediaReplaceUploadUrl(
      streamKeyOf(stream),
      replace.videoId,
      payload,
    );
    verboseUpload.beginFile(file.id, {
      name: file.data.name,
      size: file.data.size,
      uploadType: `replace/${spec.uploadType}`,
      totalParts: spec.parts && spec.parts.length,
      chunkSize: spec.chunkSize,
      stream: stream && stream.name,
      queueTotal: 1,
    });
    const onBytes = (pct) => setLocalUploadProgress(file.id, pct, file.data.size);
    let success = false;
    if (spec.uploadType === 'direct') {
      // Single-PUT upload — log it as one chunk (1/1) with timing + result.
      const ctx = verboseUpload.beginPart({ fileId: file.id, partNumber: 1, totalParts: 1, bytes: file.data.size });
      try {
        await StreamService.uploadVodMediaToBucket(spec.uploadUrl, file.data, onBytes);
        verboseUpload.endPart(ctx, { status: 200 });
      } catch (e) {
        verboseUpload.failPart(ctx, { error: e, status: e && e.response && e.response.status });
        throw e;
      }
      success = true;
    } else if (spec.uploadType === 'multipart-presigned') {
      success = await uploadMultipartPresigned(spec, file.data, onBytes, file);
    } else if (spec.uploadType === 'multipart') {
      success = await uploadMultipartAws(spec, file.data, onBytes);
      onBytes(100);
    } else {
      throw new Error(`Unsupported replace upload type: ${spec.uploadType || 'unknown'}`);
    }
    if (!success) throw new Error('Replace upload failed.');
    verboseUpload.endFile(file.id, { ok: true });

    await finalizeReplace(stream, replace, spec.uploadId, {
      file: file.data,
      mediaInfo,
      precedence: replace.precedence ?? Date.now(),
      encodingPresets: [],
    });
  } catch (e) {
    verboseUpload.endFile(file.id, { ok: false, error: e });
    // Guard setFileState (throws for removed files) so the error toast always shows.
    if (uppy.value && uppy.value.getFile(file.id)) {
      uppy.value.setFileState(file.id, { error: e?.message || 'Replace upload failed' });
      uppy.value.emit('upload-error', uppy.value.getFile(file.id), e);
    }
    notify({ group: 'error', text: e?.message || 'Video replace failed.' });
    throw e;
  } finally {
    localReplaceInFlight = Math.max(0, localReplaceInFlight - 1);
    submitting.value = false;
    window.removeEventListener('beforeunload', beforeUnload);
    recompute();
  }
};

const postProcess = async (fileIDs) => {
  for (const id of fileIDs) {
    const file = uppy.value.getFile(id);
    if (!file) continue;
    const meta = metaByFile.get(id);
    // Remote/cloud files are saved server-side by Companion — nothing to finalize
    // here, but we must still emit complete so Uppy doesn't stall.
    if (file.isRemote || !meta) { uppy.value.emit('postprocess-complete', file); continue; }
    // Only finalize (fire the `save` webhook that creates the VOD DB record) for
    // files that actually finished uploading. Failed/aborted uploads still reach
    // this post-processor — Uppy runs the uploader step with Promise.allSettled
    // and then passes the FULL fileID list to post-processors, only splitting
    // successful/failed afterwards. Without this guard we'd save a record for an
    // S3 object that was never (fully) uploaded, creating an empty/partial
    // "phantom" video. Mirrors the old UploadFiles.vue `if (success)` gate.
    if (file.error || !(file.progress && file.progress.uploadComplete)) {
      uppy.value.emit('postprocess-complete', file);
      continue;
    }
    uppy.value.emit('postprocess-progress', file, { mode: 'indeterminate', message: 'Finalizing…' });
    try {
      // Local VOD replace is finalized in runLocalReplaceUpload and remote
      // (Companion) replace in the upload-success handler — never here. This
      // post-processor only runs for normal (non-replace) VOD/scheduled uploads.
      const fileName = meta.fileName || file.name || file.data?.name || 'video.mp4';
      const fileSize = meta.fileSize || file.size || file.data?.size || 0;
      // eslint-disable-next-line no-await-in-loop
      const uploadResult = await StreamService.updateVodMediaUploadState(
        meta.spec.uploadId,
        meta.stream._id,
        streamKeyOf(meta.stream),
        {
          file: { name: fileName, size: fileSize },
          mediaInfo: meta.mediaInfo,
          precedence: meta.precedence,
          encodingPresets: [],
        },
        meta.stream._playoutDir
      );
      const fileId = uploadResult?.fileId || uploadResult?.id;
      const snapshot = { fileName, fileSize, mediaInfo: uploadResult?.mediaInfo || meta.mediaInfo };
      // Tag the new video with the sub-folder it was uploaded into (if any).
      // A failed tag degrades to the folder root — never fail the upload for it.
      if (meta.subfolder && fileId && !meta.stream._playoutDir) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await StreamService.setVideoSubfolder(meta.stream._id, fileId, meta.subfolder);
        } catch (e) {
          console.error('tag uploaded video with sub-folder failed: ', e);
        }
      }
      rememberUploadedFile(meta.stream, file, uploadResult, meta.subfolder, snapshot);
      if (fileId && !meta.stream._playoutDir) {
        // eslint-disable-next-line no-await-in-loop
        await persistUploadedFileInFolder(meta.stream, fileId, snapshot, meta.subfolder);
      }
      emitAppEvent(APP_EVENTS.VIDEO_UPLOADED, uploadResult);
      notify({ group: 'success', text: 'Video uploaded successfully.' });
    } catch (e) {
      verboseUpload.event(file.id, '✗ finalize (save) request failed after upload', {
        error: e, status: e && e.response && e.response.status,
      });
      uppy.value.emit('upload-error', file, e);
      notify({ group: 'error', text: e?.message || 'Video upload failed to save in this folder.' });
    }
    uppy.value.emit('postprocess-complete', file);
  }
};

let offEvent = null;

onMounted(async () => {
  await nextTick();
  uppy.value = new Uppy({
    autoProceed: false,
    restrictions: { allowedFileTypes: UPPY_ALLOWED_VIDEO_FILE_TYPES, maxNumberOfFiles: null },
    locale: {
      strings: {
        // Shown in Uppy's informer when a non-MP4 file is dropped/browsed.
        youCanOnlyUploadFileTypes: MP4_ONLY_ERROR_MESSAGE,
      },
    },
    onBeforeFileAdded: (file) => {
      // Remote (Dropbox/Drive) files are uploaded server-side by Companion, so
      // tag the target folder onto the Companion request URL/body.
      const stream = currentStream.value;
      const replace = replaceContext.value;
      if (file.isRemote && file.remote && stream) {
        if (file.remote.url) {
          let url = `${file.remote.url}?streamId=${stream._id}&streamKey=${stream.key}&streamType=${stream.type}`;
          if (replace?.videoId) {
            url += `&replaceVideoId=${replace.videoId}&oldFileSize=${replace.oldFileSize || 0}`;
          }
          file.remote.url = url;
        }
        if (file.remote.body) file.remote.body.encodingPresets = [];
        // Uppy v5 aws-s3 sends file.meta as the Companion `metadata`; carry the
        // real filename/content-type so Companion records them correctly.
        file.meta = { ...(file.meta || {}), key: file.name, 'content-type': file.type || 'video/mp4' };
      }
      return file;
    },
    onBeforeUpload: (files) => {
      // Local replace is uploaded manually via startUpload → runLocalReplaceUpload
      // (it keeps the file in Uppy for progress and skips uppy.upload()). This hook
      // only runs for remote (Companion/Dropbox) replaces, so just pass those
      // through; strip any stray local file so AwsS3 never grabs it.
      if (!replaceContext.value) return files;
      const next = {};
      Object.keys(files).forEach((id) => {
        if (files[id]?.isRemote) next[id] = files[id];
      });
      return next;
    },
  })
    .use(Dashboard, {
      inline: true,
      target: dashboardEl.value,
      theme: 'dark',
      height: 260,
      proudlyDisplayPoweredByUppy: false,
      showProgressDetails: true,
      hideCancelButton: false,
      note: 'MP4 (H.264) recommended for the fastest processing.',
    })
    .use(AwsS3, {
      // Companion endpoint for remote (Dropbox/Drive) files; local files use the
      // custom StreamService functions below.
      endpoint: process.env.VUE_APP_COMPANION_API_BASE_URL,
      // Uppy's default parallelism. Uploads go through a Fastly edge (not a
      // distant origin), so the uplink isn't the bottleneck — lowering this to 4
      // only made uploads slower.
      limit: 6,
      // More attempts and longer backoff than Uppy's default [0,1,3,5]s so
      // transient drops/timeouts recover instead of failing.
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
      shouldUseMultipart: (file) => {
        const meta = metaByFile.get(file.id);
        // Only Fastly presigned multipart uses these AwsS3 adapters.
        // Bare `multipart` is AWS Cognito SDK upload (handled by replace helper).
        if (meta?.spec?.uploadType === 'direct') return false;
        if (meta?.spec?.uploadType === 'multipart') return false;
        if (meta?.spec?.uploadType === 'multipart-presigned') return true;
        return file.size > MULTIPART_THRESHOLD;
      },
      // NB: Uppy passes the file *data* blob here, not the Uppy file — see
      // getChunkSizeOption. The verbose log uses the same fn so its chunk count
      // matches reality.
      getChunkSize: getChunkSizeOption,
      // Instrument each chunk PUT (multipart) and the single-PUT direct upload:
      // timing, server status/ETag and classified errors (network/CORS vs.
      // server vs. expired URL). When verbose logging is off this delegates
      // straight to Uppy's built-in uploader with zero added work, so upload
      // behaviour is unchanged.
      uploadPartBytes: (opts) => {
        if (!verboseUpload.enabled) return AwsS3.uploadPartBytes(opts);
        const ctx = verboseUpload.beginPart({ url: opts?.signature?.url, bytes: opts?.size });
        return AwsS3.uploadPartBytes(opts).then(
          (result) => {
            verboseUpload.endPart(ctx, { etag: result && (result.ETag || result.etag) });
            return result;
          },
          (err) => {
            verboseUpload.failPart(ctx, { error: err, status: err && err.source && err.source.status });
            throw err;
          },
        );
      },
      getUploadParameters: (file) => {
        const meta = metaByFile.get(file.id);
        // Correlate the single-PUT (direct) upload URL back to this queue item
        // so the verbose chunk log can label it (total is 1 for a direct upload).
        verboseUpload.registerPart(meta.spec.uploadUrl, { fileId: file.id, partNumber: 1 });
        return { method: 'PUT', url: meta.spec.uploadUrl, headers: { 'Content-Type': file.data.type } };
      },
      createMultipartUpload: (file) => {
        const meta = metaByFile.get(file.id);
        return { uploadId: meta.spec.multipartUploadId, key: String(meta.spec.uploadId) };
      },
      signPart: (file, { partNumber }) => {
        const meta = metaByFile.get(file.id);
        const part = meta.spec.parts.find((p) => p.partNumber === partNumber);
        // Correlate this part's signed URL back to the queue item + part number
        // so the low-level uploader (which only sees the URL) can label its log.
        // The total comes from the file descriptor (actual client part count).
        verboseUpload.registerPart(part.url, { fileId: file.id, partNumber });
        // Deliberately NO `expires`: @uppy/aws-s3 maps it to a hard xhr.timeout,
        // and on timeout it rejects with a *fake* 403 "Request has expired" that
        // Uppy's retry logic treats as a server rate-limit signal — it drops part
        // concurrency to 1 and keeps it pinned there, stalling the whole upload.
        // That was the main cause of the "freezes, then jumps to 100% at a
        // partial size" reports. Hang protection is kept on the raw-fetch
        // (replace) and direct axios paths, which don't feed Uppy's rate limiter.
        return {
          url: part.url,
          headers: { 'Content-Type': file.data.type },
        };
      },
      completeMultipartUpload: async (file, { parts }) => {
        const meta = metaByFile.get(file.id);
        const s = meta.stream;
        const mapped = parts.map((p) => ({ partNumber: p.PartNumber, etag: (p.ETag || '').replace(/"/g, '') }));
        try {
          if (s._playoutDir) {
            await StreamService.completeFastlyPlayoutMultipartUpload(
              s._id, s._playoutDir, meta.spec.uploadId, meta.spec.multipartUploadId, mapped, s.type
            );
          } else {
            await StreamService.completeFastlyMultipartUpload(
              streamKeyOf(s), meta.spec.uploadId, meta.spec.multipartUploadId, mapped, s.type
            );
          }
        } catch (e) {
          // Surface a failing "complete" call distinctly from a failed chunk.
          verboseUpload.event(file.id, `✗ completeMultipartUpload request failed (${mapped.length} parts)`, {
            error: e, status: e && e.response && e.response.status,
          });
          throw e;
        }
        return { location: '' };
      },
      abortMultipartUpload: async (file) => {
        const meta = metaByFile.get(file.id);
        if (!meta) return;
        const s = meta.stream;
        verboseUpload.event(file.id, '⊘ abortMultipartUpload requested');
        try {
          if (s._playoutDir) {
            await StreamService.abortFastlyPlayoutMultipartUpload(
              s._id, s._playoutDir, meta.spec.uploadId, meta.spec.multipartUploadId, s.type
            );
          } else {
            await StreamService.abortFastlyMultipartUpload(
              streamKeyOf(s), meta.spec.uploadId, meta.spec.multipartUploadId, s.type
            );
          }
        } catch (e) { /* ignore */ }
      },
    });

  uppy.value.addPreProcessor(preProcess);
  uppy.value.addPostProcessor(postProcess);

  uppy.value.on('file-added', (file) => {
    if (currentStream.value) {
      metaByFile.set(file.id, {
        stream: currentStream.value,
        subfolder: activeSubfolderFor(currentStream.value),
        fileName: file.name || file.data?.name,
        fileSize: file.size || file.data?.size,
      });
    }
    expanded.value = true;
    recompute();
    // Pre-upload quality check (pre-recorded only) so the warning shows
    // before the user commits to uploading.
    checkQuality(file);
  });
  uppy.value.on('file-removed', (file) => {
    metaByFile.delete(file.id);
    qualityWarnings.value = qualityWarnings.value.filter((w) => w.id !== file.id);
    recompute();
  });
  // ── Verbose upload logging (?verbose_upload=true) — file-level lifecycle.
  // Per-chunk timing/response/errors are logged from uploadPartBytes above; these
  // add the per-file start/finish, retries and stalls. No-ops when disabled.
  uppy.value.on('upload-success', (file) => { if (file) verboseUpload.endFile(file.id, { ok: true }); });
  uppy.value.on('upload-error', (file, error, response) => {
    verboseUpload.endFile(file && file.id, {
      ok: false, error, status: response && response.status, response,
    });
  });
  uppy.value.on('upload-retry', (fileID) => {
    verboseUpload.event(fileID, '↻ retry — Uppy will re-sign and re-send the chunk');
  });
  uppy.value.on('upload-stalled', (error, files) => {
    (files || []).forEach((f) => verboseUpload.event(f.id, '⚠ stalled — no progress within timeout', { error }));
  });
  // Non-MP4 dropped/browsed via the Uppy Dashboard: surface a clear toast
  // (in addition to Uppy's own informer). The custom "Add files" input is
  // pre-validated in onFilesPicked, so this only fires for the drop zone.
  uppy.value.on('restriction-failed', (file, error) => {
    const msg = (error && error.message) || '';
    if (msg.includes(MP4_ONLY_ERROR_MESSAGE) || /you can only upload/i.test(msg)) {
      notify({ group: 'error', text: MP4_ONLY_ERROR_MESSAGE });
    }
  });
  uppy.value.on('upload', () => { window.addEventListener('beforeunload', beforeUnload); resetSpeedSample(); recompute(); });
  uppy.value.on('progress', (pct) => { stats.progress = pct; });
  uppy.value.on('state-update', recompute);
  uppy.value.on('upload-success', async (file, response) => {
    // Cloud/Companion replace: companion returns the new video id; finalize here.
    const replace = replaceContext.value;
    const stream = currentStream.value;
    if (!replace?.videoId || !file?.isRemote || !stream) return;
    const body = (response && response.body) || {};
    const newVideoId = body.videoId || body.id;
    if (!newVideoId) return;
    try {
      await finalizeReplace(stream, replace, newVideoId, {
        file: {
          name: replace.fileName || file.name,
          size: body.bytes || file.size,
        },
        mediaInfo: body.mediaInfo || body.mediainfo,
        precedence: replace.precedence,
        encodingPresets: body.encodingPresets || [],
        bytes: body.bytes || file.size,
      });
    } catch (e) { /* surfaced via refresh / next attempt */ }
  });

  uppy.value.on('complete', async () => {
    // Local replace runs outside Uppy — finalizeReplace reloads the page.
    if (localReplaceInFlight > 0) {
      recompute();
      return;
    }
    stats.progress = 100;
    submitting.value = false;
    window.removeEventListener('beforeunload', beforeUnload);
    try { await store.dispatch('Streams/getStreams'); } catch (e) { /* noop */ }
    const uploadedStreamId = currentStream.value?._id || router.currentRoute.value.params?.streamId;
    await reconcileUploadedFiles(uploadedStreamId);
    if (pendingUploadedIds.size) scheduleUploadReconcile(uploadedStreamId);
    const replace = replaceContext.value;
    emitAppEvent(
      APP_EVENTS.VIDEO_UPLOADED,
      replace?.videoId ? { replaced: true, videoId: replace.videoId } : undefined,
    );
    // Replace is single-shot — clear context once the batch finishes.
    if (replace) replaceContext.value = null;
    recompute();
  });

  offEvent = onAppEvent(APP_EVENTS.OPEN_VOD_UPLOADER, (payload) => {
    if (payload?.stream) currentStream.value = payload.stream;
    replaceContext.value = payload?.replace || null;
    // Replace mode: only one file. Otherwise allow the usual multi-file queue.
    if (uppy.value) {
      uppy.value.setOptions({
        restrictions: {
          allowedFileTypes: UPPY_ALLOWED_VIDEO_FILE_TYPES,
          maxNumberOfFiles: replaceContext.value ? 1 : null,
        },
      });
    }
    // Show/hide the Dropbox tab based on context (VOD-only)
    syncCloudProvider();
    // If the previous batch is fully finished, clear it so opening for a new
    // folder starts on a clean empty state (not the old completed list).
    if (uppy.value && allDone.value) {
      if (uppy.value.clear) uppy.value.clear(); else uppy.value.cancelAll();
      metaByFile.clear();
      clearQualityWarnings();
      clearBitrateWarning();
      resetSpeedSample();
    }
    expanded.value = true;
    // Files supplied by the caller (e.g. TV Playout dir modal) → stage them.
    if (payload?.files && payload.files.length && uppy.value) {
      payload.files.forEach((f) => {
        try { uppy.value.addFile({ name: f.name, type: f.type, data: f, source: 'Local' }); } catch (e) { /* dup/restriction */ }
      });
      return;
    }
    // Fresh open with nothing staged → jump straight to the OS file picker
    // (runs inside the originating click, so it keeps user-activation).
    if (uppy.value && uppy.value.getFiles().length === 0) pickFiles();
  });
});

onBeforeUnmount(() => {
  if (offEvent) offEvent();
  uploadReconcileTimers.forEach((timer) => clearTimeout(timer));
  window.removeEventListener('beforeunload', beforeUnload);
  if (uppy.value) { uppy.value.destroy(); uppy.value = null; }
  metaByFile.clear();
});

const expand = () => { expanded.value = true; };
// `submitting` = the prep phase (MediaInfo inspect + signed-URL request) between
// clicking Upload and the first byte, so the button isn't a dead "Upload" state.
const submitting = ref(false);
// Any not-yet-started file can be uploaded — even while others are uploading
// (lets you queue uploads to a different folder mid-run).
const canUpload = computed(() => stats.pending > 0 && !submitting.value);
const startUpload = () => {
  if (!uppy.value) return;
  submitting.value = true;
  // VOD replace: local files are uploaded manually (direct / multipart-presigned /
  // AWS multipart) so they stay in Uppy for progress and don't hit the AwsS3/Fastly
  // path. Remote (Dropbox) replaces still go through Companion via uppy.upload().
  if (replaceContext.value) {
    const files = uppy.value.getFiles();
    const locals = files.filter((f) => !f.isRemote && !(f.progress && f.progress.uploadStarted));
    const hasRemote = files.some((f) => f.isRemote);
    locals.forEach((f) => { runLocalReplaceUpload(f).catch(() => { /* surfaced in helper */ }); });
    if (hasRemote) uppy.value.upload();
    else if (!locals.length) submitting.value = false;
    return;
  }
  uppy.value.upload();
};
watch(() => stats.uploading, (v) => { if (v) submitting.value = false; });
const closeWidget = () => {
  // If uploads are in flight, collapse to the chip instead of cancelling.
  if (stats.uploading) { expanded.value = false; return; }
  if (uppy.value) uppy.value.clear ? uppy.value.clear() : uppy.value.cancelAll();
  metaByFile.clear();
  clearQualityWarnings();
  clearBitrateWarning();
  replaceContext.value = null;
  if (uppy.value) {
    uppy.value.setOptions({
      restrictions: { allowedFileTypes: UPPY_ALLOWED_VIDEO_FILE_TYPES, maxNumberOfFiles: null },
    });
  }
  expanded.value = false;
  recompute();
};

// Explicit, destructive stop for in-flight uploads (distinct from Minimize).
// Aborts all transfers (incl. S3 multipart via abortMultipartUpload) and resets.
const cancelUploads = () => {
  if (!stats.uploading && !submitting.value) return;
  if (!window.confirm('Cancel upload? Progress will be lost.')) return;
  if (uppy.value) uppy.value.cancelAll();
  window.removeEventListener('beforeunload', beforeUnload);
  metaByFile.clear();
  clearQualityWarnings();
  clearBitrateWarning();
  resetSpeedSample();
  submitting.value = false;
  recompute();
};

const headerLabel = computed(() => {
  if (allDone.value) return stats.failed ? `${stats.completed} done · ${stats.failed} failed` : 'Uploads complete';
  if (stats.uploading) return 'Uploading…';
  if (submitting.value) return 'Preparing…';
  return stats.total > 0 ? `${stats.total} ${stats.total === 1 ? 'file' : 'files'} ready` : 'Upload videos';
});
</script>

<template>
  <!-- Kept in the DOM so the Uppy Dashboard target persists while minimized -->
  <div class="fixed bottom-24 right-4 z-[60]" :class="visible ? '' : 'pointer-events-none'">
    <!-- Minimized chip -->
    <FormButton v-show="showChip" @click="expand"
      custom-classes="flex items-center gap-2.5 bg-surface-2 ring-1 ring-surface-3 shadow-xl rounded-full pl-3 pr-4 py-2 hover:bg-surface-3 transition-colors">
      <span v-if="!allDone" class="relative inline-flex size-5 items-center justify-center">
        <span class="inline-block size-4 rounded-full border-2 border-main/40 border-t-main animate-spin"></span>
      </span>
      <span v-else class="inline-flex size-5 items-center justify-center text-success">
        <icon-base :width="16" :height="16" fill="none"><icon-checkmark-stroke /></icon-base>
      </span>
      <span class="text-sm font-medium text-white">{{ headerLabel }}</span>
      <span v-if="!allDone && stats.progress > 0" class="text-tiny text-surface-7 tabular-nums">{{ Math.round(stats.progress) }}%</span>
    </FormButton>

    <!-- Expanded panel -->
    <div v-show="expanded"
      class="w-[min(460px,calc(100vw-2rem))] bg-surface-2 ring-1 ring-surface-3 rounded-xl shadow-2xl overflow-hidden flex flex-col">
      <header class="flex items-center gap-2 px-4 py-3">
        <icon-base class="text-surface-7 shrink-0" width="16" height="16" fill="none"><icon-upload /></icon-base>
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium text-white truncate leading-tight">{{ headerLabel }}</div>
          <div v-if="folderName" class="mt-1 flex items-center gap-1 min-w-0">
            <span class="text-[10px] font-medium uppercase tracking-wide text-surface-6 shrink-0">To</span>
            <span class="inline-flex items-center gap-1 min-w-0 rounded-md bg-surface-3 pl-1.5 pr-2 py-0.5">
              <icon-base class="shrink-0 text-main" :width="12" :height="12" width-view-box="24" height-view-box="24" fill="none"><icon-folder-outline /></icon-base>
              <span class="text-tiny font-semibold text-surface-9 truncate">{{ folderName }}</span>
            </span>
          </div>
        </div>
        <FormButton @click="pickFiles"
          custom-classes="ml-auto inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md bg-main hover:bg-main-light text-white text-tiny font-medium transition-colors">
          <icon-base fill="none" width="14" height="14"><icon-plus /></icon-base>
          Add files
        </FormButton>
        <input ref="fileInput" type="file" :accept="VIDEO_UPLOAD_ACCEPT" multiple class="hidden" @change="onFilesPicked" />
        <FormButton :title="(stats.uploading || submitting) ? 'Minimize' : 'Close'" @click="closeWidget"
          custom-classes="size-7 rounded-md flex items-center justify-center text-surface-7 hover:text-white hover:bg-surface-3 transition-colors">
          <icon-base fill="none">
            <icon-chevron-down v-if="stats.uploading || submitting" />
            <icon-cross v-else />
          </icon-base>
        </FormButton>
      </header>
      <!-- Dashboard target: always in DOM so Uppy stays mounted while minimized -->
      <div class="relative">
        <div ref="dashboardEl" class="uppy-vod-uploader"></div>
      </div>

      <!-- Bitrate rejection notice: in the flow below the dashboard so it never covers the provider tiles -->
      <div
        v-if="bitrateWarning"
        class="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-error-transparent ring-1 ring-error-light px-3 py-2"
        :class="{ 'mb-3': !stats.total }"
      >
        <icon-base class="shrink-0 mt-0.5 text-error" :width="15" :height="15" width-view-box="16" height-view-box="15"><icon-alert-triangle /></icon-base>
        <div class="min-w-0 flex-1 text-tiny text-surface-8 mb-0 leading-snug">
          <p class="mb-0.5 font-medium text-error">Video bitrate too high</p>
          <p class="mb-0">
            <span class="text-surface-9 font-medium">{{ bitrateWarning.fileName }}</span>
            is {{ bitrateWarning.kbps.toLocaleString() }} kbps — max supported is
            {{ MAX_VIDEO_BITRATE_KBPS.toLocaleString() }} kbps. Re-encode at a lower bitrate and try again.
          </p>
        </div>
        <FormButton
          title="Dismiss"
          custom-classes="shrink-0 size-6 rounded-md flex items-center justify-center text-surface-7 hover:text-white hover:bg-surface-3 transition-colors"
          @click="clearBitrateWarning"
        >
          <icon-base fill="none" width="12" height="12"><icon-cross /></icon-base>
        </FormButton>
      </div>

      <!-- Non-blocking quality notice for pre-recorded uploads (CBR/audio) -->
      <div v-if="hasQualityWarning" class="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-surface-1 ring-1 ring-surface-3 px-3 py-2">
        <icon-base class="shrink-0 mt-0.5 text-warning" :width="15" :height="15" width-view-box="16" height-view-box="15"><icon-alert-triangle /></icon-base>
        <div class="text-tiny text-surface-8 mb-0 leading-snug">
          <p class="mb-0.5 font-medium text-surface-9">This file doesn't match our recommended settings</p>
          <p class="mb-0">
            Playback may be unstable — {{ qualityIssue }}. For best results, re-encode using our
            <a href="https://docs.castr.com/en/articles/5161326-recommended-settings-for-recorded-videos" target="_blank" class="underline text-surface-9">recommended settings</a>.
            If you'd rather use it as-is, run a quick test stream first.
          </p>
        </div>
      </div>

      <!-- Custom action bar (replaces Uppy's StatusBar): fixed height = no shift -->
      <div v-if="stats.total > 0" class="px-4 py-3">
        <div v-if="stats.uploading || allDone || submitting" class="h-1.5 rounded-full bg-surface-1 overflow-hidden mb-2.5">
          <div v-if="submitting && !stats.uploading" class="h-full w-1/3 rounded-full bg-main uppy-prep-bar"></div>
          <div v-else class="h-full rounded-full transition-all duration-300"
            :class="(allDone && !stats.failed) ? 'bg-success' : 'bg-main'"
            :style="{ width: (allDone ? 100 : Math.max(2, Math.round(stats.progress))) + '%' }"></div>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-tiny text-surface-7 truncate flex-1">
            <template v-if="stats.uploading">
              {{ Math.round(stats.progress) }}% · {{ stats.completed }}/{{ stats.total }} · {{ fmtBytes(stats.uploadedBytes) }} of {{ fmtBytes(stats.totalBytes) }}<template v-if="stats.speed"> · {{ fmtBytes(stats.speed) }}/s<template v-if="stats.eta"> · {{ fmtEta(stats.eta) }} left</template></template>
            </template>
            <template v-else-if="submitting">Preparing files…</template>
            <template v-else-if="allDone">{{ stats.failed ? `${stats.completed} uploaded · ${stats.failed} failed` : `${stats.completed} ${stats.completed === 1 ? 'file' : 'files'} uploaded` }}</template>
            <template v-else>{{ stats.pending }} {{ stats.pending === 1 ? 'file' : 'files' }} ready · {{ fmtBytes(stats.totalBytes) }}</template>
          </span>
          <div class="ml-auto flex items-center gap-2 shrink-0">
            <!-- Cancel: destructive stop for in-flight uploads (distinct from Minimize) -->
            <FormButton v-if="stats.uploading || submitting" @click="cancelUploads"
              custom-classes="h-9 px-3 rounded-lg text-surface-8 hover:text-white hover:bg-surface-3 text-sm font-medium transition-colors">
              Cancel
            </FormButton>
            <!-- Upload button shows whenever files are staged (even mid-upload) -->
            <FormButton v-if="canUpload" @click="startUpload"
              custom-classes="h-9 px-4 rounded-lg bg-main hover:bg-main-light text-white text-sm font-semibold transition-colors">
              Upload {{ stats.pending }} {{ stats.pending === 1 ? 'file' : 'files' }}
            </FormButton>
            <FormButton v-else-if="allDone" @click="closeWidget"
              custom-classes="h-9 px-4 rounded-lg bg-surface-3 hover:bg-surface-4 text-white text-sm font-medium transition-colors">
              Done
            </FormButton>
            <span v-else-if="submitting" class="inline-flex items-center">
              <span class="inline-block size-4 rounded-full border-2 border-main/40 border-t-main animate-spin"></span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Indeterminate "preparing" progress sweep */
.uppy-prep-bar { animation: uppy-prep 1.1s ease-in-out infinite; }
@keyframes uppy-prep {
  0% { transform: translateX(-120%); }
  100% { transform: translateX(320%); }
}
</style>

<style>
/* ── Castr dark theme for the Uppy Dashboard ─────────────────────────────── */
.uppy-vod-uploader .uppy-Dashboard-inner,
.uppy-vod-uploader .uppy-Dashboard-innerWrap {
  background-color: var(--surface-1);
  border: none;
  border-radius: 0;
}
/* Make the drop area fill the panel so its content centers vertically */
.uppy-vod-uploader .uppy-Dashboard-inner { height: 260px; }
.uppy-vod-uploader .uppy-Dashboard-innerWrap { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.uppy-vod-uploader .uppy-Dashboard-AddFiles {
  flex: 1 1 auto;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
}
/* We render our own header + action bar; hide Uppy's content bar (Cancel /
   "Uploading N" / +) and StatusBar. Hiding the StatusBar also lets the
   empty-state drop area fill + center vertically. */
.uppy-vod-uploader .uppy-DashboardContent-bar,
.uppy-vod-uploader .uppy-StatusBar,
.uppy-vod-uploader .uppy-Dashboard-progressindicators { display: none !important; }
/* …but keep the bar inside provider panels (Dropbox) — it holds the Back button */
.uppy-vod-uploader .uppy-DashboardContent-panel .uppy-DashboardContent-bar {
  display: flex !important;
  background-color: var(--surface-2);
}
/* Keep the file list compact with its own scroll */
.uppy-vod-uploader .uppy-Dashboard-files { max-height: 264px; overflow-y: auto; }
.uppy-vod-uploader .uppy-DashboardContent-title,
.uppy-vod-uploader .uppy-StatusBar-statusPrimary { color: var(--surface-9); }
.uppy-vod-uploader .uppy-Dashboard-AddFiles {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  background-color: var(--surface-1);
  border-radius: 12px;
  margin: 12px;
  transition: box-shadow 0.15s ease, background-color 0.15s ease;
}
/* Brand ring only while dragging a file over the zone */
.uppy-vod-uploader .uppy-Dashboard--isDraggingOver .uppy-Dashboard-AddFiles {
  background-color: var(--c-main-transparent);
  box-shadow: inset 0 0 0 2px var(--c-main);
}
.uppy-vod-uploader .uppy-Dashboard-AddFiles-info { position: static; margin-top: 6px; }
.uppy-vod-uploader .uppy-Dashboard-AddFiles-title {
  position: static;
  margin: 0;
  padding: 0;
  max-width: 340px;
  font-size: 0.95rem;
  color: var(--surface-8);
}
/* Upload icon above the prompt */
.uppy-vod-uploader .uppy-Dashboard-AddFiles-title::before {
  content: "";
  display: block;
  width: 52px;
  height: 52px;
  margin: 0 auto 16px;
  border-radius: 9999px;
  background: var(--c-main-transparent) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%230062ff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 16V4M6 10l6-6 6 6M4 20h16'/%3E%3C/svg%3E") center / 24px no-repeat;
}
.uppy-vod-uploader .uppy-Dashboard-note {
  position: static;
  margin: 4px auto 0;
  max-width: 320px;
  color: var(--surface-6);
}
/* Cloud source(s) shown as centered chips (no divider line) */
.uppy-vod-uploader .uppy-Dashboard-AddFiles-list {
  display: flex !important;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 0 !important;
  padding: 0 !important;
  border: none !important;
}
.uppy-vod-uploader .uppy-DashboardTab { width: auto; margin: 0; padding: 0; border: none !important; }
.uppy-vod-uploader .uppy-DashboardTab-btn {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: auto;
  height: auto;
  min-height: 0;
  padding: 8px 14px;
  border-radius: 8px;
  background-color: var(--surface-3) !important;
  color: var(--surface-9) !important;
  border: none !important;
}
.uppy-vod-uploader .uppy-DashboardTab-btn:hover { background-color: var(--surface-4) !important; }
.uppy-vod-uploader .uppy-DashboardTab-btn svg { width: 18px !important; height: 18px !important; margin: 0 !important; }
.uppy-vod-uploader .uppy-DashboardTab-name { font-size: 0.8125rem !important; margin: 0 !important; }
.uppy-vod-uploader .uppy-Dashboard-browse,
.uppy-vod-uploader .uppy-Dashboard-AddFiles-title button,
.uppy-vod-uploader .uppy-DashboardContent-addMore,
.uppy-vod-uploader .uppy-DashboardContent-back {
  color: var(--c-main) !important;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.uppy-vod-uploader .uppy-Dashboard-browse:hover,
.uppy-vod-uploader .uppy-Dashboard-AddFiles-title button:hover { color: var(--c-main-light) !important; }
/* Drop the oversized file-preview thumbnail; keep the list compact + row-based */
.uppy-vod-uploader .uppy-Dashboard-Item-preview { display: none !important; }
.uppy-vod-uploader .uppy-Dashboard-Item {
  flex-direction: row;
  align-items: center;
  min-height: 0;
  border: none !important;
  border-radius: 8px;
  margin: 2px 8px;
  padding: 8px 10px;
}
.uppy-vod-uploader .uppy-Dashboard-Item:hover { background-color: var(--surface-1); }
.uppy-vod-uploader .uppy-Dashboard-files { padding: 4px 0; }
.uppy-vod-uploader .uppy-Dashboard-Item-fileInfoAndButtons { padding: 0; }
.uppy-vod-uploader .uppy-Dashboard-Item-name {
  color: var(--surface-9) !important;
  font-size: 0.875rem;
  font-weight: 500;
}
.uppy-vod-uploader .uppy-Dashboard-Item-status,
.uppy-vod-uploader .uppy-Dashboard-Item-statusSize {
  color: var(--surface-7) !important;
  font-size: 0.75rem;
}
/* "N files selected" title in the top bar */
.uppy-vod-uploader .uppy-DashboardContent-title { color: var(--surface-9) !important; font-weight: 500; }

/* Upload button (pre-upload "Upload N files") → brand blue */
.uppy-vod-uploader .uppy-StatusBar-actionBtn--upload,
.uppy-vod-uploader .uppy-StatusBar.is-waiting .uppy-StatusBar-actionBtn,
.uppy-vod-uploader .uppy-c-btn-primary {
  background-color: var(--c-main) !important;
  color: #fff !important;
  border-radius: 8px !important;
  padding: 9px 18px !important;
  font-weight: 600 !important;
}
.uppy-vod-uploader .uppy-StatusBar-actionBtn--upload:hover,
.uppy-vod-uploader .uppy-StatusBar.is-waiting .uppy-StatusBar-actionBtn:hover,
.uppy-vod-uploader .uppy-c-btn-primary:hover { background-color: var(--c-main-light) !important; }
.uppy-vod-uploader .uppy-StatusBar-progress { background-color: var(--c-main); }
.uppy-vod-uploader .uppy-StatusBar-actionCircleBtn { background: transparent; }
.uppy-vod-uploader .uppy-Dashboard-Item-action { color: var(--surface-7); }
</style>
